import {
  Directive,
  ElementRef,
  Renderer2,
  OnInit,
  OnDestroy,
  inject,
  input,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { Subscription } from 'rxjs';

import type { TooltipPosition } from '@shared-models';

import { TooltipService } from '../../services/tooltip/tooltip.service';

@Directive({
  selector: '[appFormError]',
  standalone: true,
})
export class FormErrorDirective implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ngControl = inject(NgControl, { optional: true });
  private tooltipService = inject(TooltipService);

  errorMessage = input<string>('Invalid field', { alias: 'appFormError' });
  errorIconPosition = input<
    'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  >('top-right');
  showIcon = input<boolean>(true);

  private errorIconElement: HTMLElement | null = null;
  private tooltipElement: HTMLElement | null = null;
  private statusChangeSubscription: Subscription | null = null;
  private parentContainer: HTMLElement | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private autoHideTimeout: ReturnType<typeof setTimeout> | null = null;
  private removeScrollListener: (() => void) | null = null;

  ngOnInit(): void {
    if (!this.ngControl) {
      console.warn('FormErrorDirective: No NgControl found in element');
      return;
    }

    this.setupParentContainer();
    this.subscribeToStatusChanges();

    setTimeout(() => this.updateErrorState(), 0);
  }

  ngOnDestroy(): void {
    if (this.statusChangeSubscription) {
      this.statusChangeSubscription.unsubscribe();
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
    }
    this.hideErrorIcon();
  }

  private setupParentContainer(): void {
    const parentElement = this.elementRef.nativeElement.parentElement;
    const computedStyle = window.getComputedStyle(parentElement);

    if (computedStyle.position === 'static') {
      this.renderer.setStyle(parentElement, 'position', 'relative');
    }

    this.parentContainer = parentElement;
  }

  private subscribeToStatusChanges(): void {
    if (this.ngControl && this.ngControl.statusChanges) {
      this.statusChangeSubscription = this.ngControl.statusChanges.subscribe(
        () => {
          this.updateErrorState();
        },
      );
    }
  }

  private updateErrorState(): void {
    const control = this.ngControl?.control;

    if (control && control.invalid && (control.dirty || control.touched)) {
      this.displayErrorIcon();
    } else {
      this.hideErrorIcon();
    }
  }

  private displayErrorIcon(): void {
    if (this.errorIconElement || !this.showIcon()) {
      return;
    }

    // Create error icon element
    this.errorIconElement = this.renderer.createElement('div');
    this.renderer.addClass(this.errorIconElement, 'v-one-form-error-icon');
    this.renderer.addClass(
      this.errorIconElement,
      `v-one-form-error-icon--${this.errorIconPosition()}`,
    );

    // Desktop hover listeners
    this.renderer.listen(this.errorIconElement, 'mouseenter', () => {
      if (this.tooltipService.isDesktop()) {
        this.showTooltip();
      }
    });

    this.renderer.listen(this.errorIconElement, 'mouseleave', () => {
      if (this.tooltipService.isDesktop()) {
        this.hideTooltip();
      }
    });

    // Mobile and desktop fallback click listener
    this.renderer.listen(this.errorIconElement, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.tooltipElement) {
        this.hideTooltip();
      } else {
        this.showTooltip();
        // Auto-hide after 3 seconds on mobile/click
        this.autoHideTimeout = setTimeout(() => {
          this.hideTooltip();
        }, 3000);
      }
    });

    if (this.parentContainer) {
      this.renderer.appendChild(this.parentContainer, this.errorIconElement);
    }
  }

  private hideErrorIcon(): void {
    this.hideTooltip();

    if (this.errorIconElement && this.parentContainer) {
      this.renderer.removeChild(this.parentContainer, this.errorIconElement);
      this.errorIconElement = null;
    }
  }

  private showTooltip(): void {
    if (this.tooltipElement || !this.errorIconElement) {
      return;
    }

    // Use default message if errorMessage is empty
    const messageToShow =
      this.errorMessage() && this.errorMessage().trim() !== ''
        ? this.errorMessage()
        : 'Invalid field';

    // Create tooltip using service with delay
    this.timeoutId = setTimeout(() => {
      if (this.errorIconElement) {
        this.tooltipElement = this.tooltipService.createTooltip({
          hostElement: this.errorIconElement,
          message: messageToShow,
          position: this.getTooltipPosition(),
          severity: 'default',
          renderer: this.renderer,
        });

        // Add scroll listener for auto-hide
        this.removeScrollListener = this.tooltipService.addScrollListener(
          this.errorIconElement,
          () => this.hideTooltip(),
        );
      }
    }, 200);
  }

  private hideTooltip(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    // Remove scroll listener
    if (this.removeScrollListener) {
      this.removeScrollListener();
      this.removeScrollListener = null;
    }

    if (this.tooltipElement) {
      this.tooltipService.hideTooltip(this.tooltipElement, this.renderer);
      this.tooltipElement = null;
    }
  }

  /**
   * Determines tooltip position based on icon position and device type
   * Mobile: Inverts position for better space utilization
   */
  private getTooltipPosition(): TooltipPosition {
    let basePosition: 'left' | 'right';

    if (this.errorIconPosition().includes('right')) {
      basePosition = 'right';
    } else {
      basePosition = 'left';
    }

    // Mobile: Invert position for better space utilization
    if (this.isMobile()) {
      return basePosition === 'right' ? 'left' : 'right';
    }

    return basePosition;
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }
}
